import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ticketsApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Ticket, Plus, MagnifyingGlass, Funnel, ArrowClockwise,
  Clock, WarningCircle, CheckCircle, Circle,
} from '@phosphor-icons/react';
import { Toaster } from 'sonner';

const priorityConfig = {
  critical: { label: 'Critical', cls: 'bg-red-100 text-red-700 border-red-200' },
  high: { label: 'High', cls: 'bg-orange-100 text-orange-700 border-orange-200' },
  medium: { label: 'Medium', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  low: { label: 'Low', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const statusConfig = {
  open: { label: 'Open', cls: 'bg-blue-100 text-blue-700' },
  assigned: { label: 'Assigned', cls: 'bg-indigo-100 text-indigo-700' },
  in_progress: { label: 'In Progress', cls: 'bg-violet-100 text-violet-700' },
  waiting_for_user: { label: 'Waiting for User', cls: 'bg-amber-100 text-amber-700' },
  waiting_for_payer: { label: 'Waiting for Payer', cls: 'bg-orange-100 text-orange-700' },
  escalated: { label: 'Escalated', cls: 'bg-red-100 text-red-700' },
  resolved: { label: 'Resolved', cls: 'bg-emerald-100 text-emerald-700' },
  closed: { label: 'Closed', cls: 'bg-slate-100 text-slate-600' },
  reopened: { label: 'Reopened', cls: 'bg-rose-100 text-rose-700' },
};

const TicketsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ status: '', priority: '', category: '', search: '', my_tickets: false });
  const [categories, setCategories] = useState([]);

  const isAdmin = user?.roles?.includes('hospital_admin') || user?.role === 'hospital_admin';

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (filters.status) params.status = filters.status;
      if (filters.priority) params.priority = filters.priority;
      if (filters.category) params.category = filters.category;
      if (filters.search) params.search = filters.search;
      if (filters.my_tickets) params.my_tickets = true;
      const res = await ticketsApi.list(params);
      setTickets(res.data.tickets || []);
      setTotal(res.data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, filters]);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  useEffect(() => {
    const loadCats = async () => {
      try { const res = await ticketsApi.categories(); setCategories(res.data.categories || []); } catch {}
    };
    loadCats();
  }, []);

  const clearFilters = () => {
    setFilters({ status: '', priority: '', category: '', search: '', my_tickets: false });
    setPage(1);
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-5" data-testid="tickets-page">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-900 via-slate-900 to-violet-900 rounded-2xl p-6 text-white" data-testid="tickets-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-indigo-500/20 flex items-center justify-center border border-indigo-500/30">
              <Ticket className="h-6 w-6 text-indigo-400" weight="duotone" />
            </div>
            <div>
              <h1 className="text-xl font-bold font-['Outfit']" data-testid="tickets-heading">Ticket Management</h1>
              <p className="text-sm text-slate-300 mt-0.5">Track and resolve support tickets, claim issues, and requests</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Badge className="bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 text-xs">{total} Total</Badge>
            <Button onClick={() => navigate('/tickets/new')} className="bg-indigo-500 hover:bg-indigo-600 text-white gap-1.5 text-sm"
              data-testid="new-ticket-btn">
              <Plus className="h-4 w-4" /> New Ticket
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white border border-slate-200 shadow-sm" data-testid="filters-card">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search tickets..." className="pl-9 border-slate-200 h-9 text-sm"
                value={filters.search} onChange={e => setFilters(f => ({...f, search: e.target.value}))}
                data-testid="search-input" onKeyDown={e => e.key === 'Enter' && loadTickets()} />
            </div>
            <Select value={filters.status} onValueChange={v => setFilters(f => ({...f, status: v === 'all' ? '' : v}))}>
              <SelectTrigger className="w-[150px] h-9 text-sm border-slate-200" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {Object.entries(statusConfig).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.priority} onValueChange={v => setFilters(f => ({...f, priority: v === 'all' ? '' : v}))}>
              <SelectTrigger className="w-[130px] h-9 text-sm border-slate-200" data-testid="filter-priority">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                {Object.entries(priorityConfig).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.category} onValueChange={v => setFilters(f => ({...f, category: v === 'all' ? '' : v}))}>
              <SelectTrigger className="w-[180px] h-9 text-sm border-slate-200" data-testid="filter-category">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant={filters.my_tickets ? "default" : "outline"} size="sm" className="h-9 text-xs gap-1"
              onClick={() => setFilters(f => ({...f, my_tickets: !f.my_tickets}))} data-testid="my-tickets-btn">
              {filters.my_tickets ? 'My Tickets' : 'All Tickets'}
            </Button>
            <Button variant="ghost" size="sm" className="h-9 text-xs gap-1 text-slate-500" onClick={clearFilters}>
              <ArrowClockwise className="h-3.5 w-3.5" /> Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-white border border-slate-200 shadow-sm" data-testid="tickets-table">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500" /></div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Ticket className="h-10 w-10 mx-auto text-slate-300 mb-2" />
              <p className="text-sm font-medium">No tickets found</p>
              <p className="text-xs text-slate-400 mt-1">Create a new ticket to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold w-[100px]">Ticket ID</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Subject</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">Category</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">Priority</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">Status</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Assigned To</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">SLA Due</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tickets.map(t => (
                  <TableRow key={t.id} className="hover:bg-indigo-50/30 cursor-pointer" onClick={() => navigate(`/tickets/${t.id}`)}
                    data-testid={`ticket-row-${t.ticket_id}`}>
                    <TableCell>
                      <span className="text-xs font-mono font-semibold text-indigo-600">{t.ticket_id}</span>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm font-medium text-slate-800 truncate max-w-[250px]">{t.subject}</p>
                      {t.related_claim_no && <p className="text-[10px] text-slate-400">Claim: {t.related_claim_no}</p>}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-[10px] text-slate-600 bg-slate-100 px-2 py-0.5 rounded">{t.category}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={`${priorityConfig[t.priority]?.cls || ''} border text-[10px]`}>
                        {priorityConfig[t.priority]?.label || t.priority}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={`${statusConfig[t.status]?.cls || ''} border-0 text-[10px]`}>
                        {statusConfig[t.status]?.label || t.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-slate-600">{t.assigned_to_name || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {t.is_overdue && <WarningCircle className="h-3.5 w-3.5 text-red-500" weight="fill" />}
                        <span className={`text-xs ${t.is_overdue ? 'text-red-600 font-medium' : 'text-slate-500'}`}>
                          {t.sla_due ? formatDate(t.sla_due) : '-'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-slate-500">{formatDate(t.created_at)}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > 50 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-500">Showing {(page-1)*50+1}-{Math.min(page*50, total)} of {total}</p>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p-1)}>Prev</Button>
            <Button variant="outline" size="sm" disabled={page*50 >= total} onClick={() => setPage(p => p+1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketsPage;
